// ═══════════════════════════════════════════════════════════════════════════
//  OPTION CHAIN UTILITIES — ERP Options Engine
// ═══════════════════════════════════════════════════════════════════════════
import type { OcExpiry, OcStrike } from './ChartDataTypes';
import { computeMaxPain } from '../../lib/options';

export const OC_EXPIRY_TABS: { key: OcExpiry; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: 'next',    label: 'Next'    },
  { key: 'monthly', label: 'Monthly' },
  { key: 'far',     label: 'Far'     },
];

export const ocFmtOI  = (n: number): string =>
  n >= 100000 ? `${(n / 100000).toFixed(2)}L` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);
export const ocFmtVol = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

export function ocStep(sym: string): number {
  if (/BANK.?NIFTY|BANKEX/i.test(sym)) return 100;
  if (/SENSEX/i.test(sym))             return 100;
  if (/GOLDM|SILVERM/i.test(sym))      return 100;
  if (/GOLD/i.test(sym))               return 100;
  if (/SILVER/i.test(sym))             return 250;
  if (/CRUDEOIL/i.test(sym))           return 50;
  if (/NATURALGAS/i.test(sym))         return 5;
  if (/COPPER/i.test(sym))             return 5;
  if (/ZINC/i.test(sym))               return 2.5;
  return 50;
}

export function ocOptionBase(sym: string): string {
  if (/BANK.?NIFTY/i.test(sym))  return 'BANKNIFTY';
  if (/FIN.?NIFTY/i.test(sym))   return 'FINNIFTY';
  if (/MIDCPNIFTY/i.test(sym))   return 'MIDCPNIFTY';
  if (/NIFTY/i.test(sym))        return 'NIFTY';
  if (/BANKEX/i.test(sym))       return 'BANKEX';
  if (/SENSEX/i.test(sym))       return 'SENSEX';
  return sym.replace(/\s+/g, '').toUpperCase();
}

export function ocDefaultSpot(sym: string): number {
  if (/BANK.?NIFTY/i.test(sym))  return 52000;
  if (/FIN.?NIFTY/i.test(sym))   return 23800;
  if (/NIFTY/i.test(sym))        return 24500;
  if (/SENSEX/i.test(sym))       return 80500;
  if (/BANKEX/i.test(sym))       return 57000;
  if (/GOLD/i.test(sym))         return 157000;
  if (/SILVER/i.test(sym))       return 158000;
  if (/CRUDEOIL/i.test(sym))     return 5800;
  if (/NATURALGAS/i.test(sym))   return 280;
  if (/COPPER/i.test(sym))       return 850;
  if (/ZINC/i.test(sym))         return 280;
  return 1500;
}

export function buildOcChain(sym: string, spot: number, dte: number): OcStrike[] {
  const step = ocStep(sym);
  const atm  = Math.round(spot / step) * step;
  const T    = Math.max(1, dte) / 365;
  const atmLtp = spot * 0.145 * Math.sqrt(T) * 0.4;

  return Array.from({ length: 11 }, (_, idx) => {
    const i       = idx - 5;
    const strike  = atm + i * step;
    const skew    = i < 0 ? Math.abs(i) * 0.9 : i * 0.45;
    const callIV  = +(14.5 + skew + Math.random() * 0.8).toFixed(2);
    const putIV   = +(15.0 + skew + (i < 0 ? 1.2 : 0) + Math.random() * 0.8).toFixed(2);
    const cDelta  = +Math.max(0.01, Math.min(0.99, 0.5 - i * 0.09)).toFixed(2);
    const pDelta  = +(cDelta - 1).toFixed(2);
    const tv      = atmLtp * Math.exp(-0.38 * Math.pow(i, 2));
    const cLtp    = +Math.max(0.5, Math.max(0, spot - strike) + tv + (Math.random() - 0.1) * 2).toFixed(2);
    const pLtp    = +Math.max(0.5, Math.max(0, strike - spot) + tv + (Math.random() - 0.1) * 2).toFixed(2);
    const oiBase  = Math.round(330000 * Math.exp(-0.28 * Math.pow(i, 2)) + Math.random() * 80000);
    const cOI     = Math.round(oiBase * (i >= 0 ? 1.12 : 0.88) + Math.random() * 40000);
    const pOI     = Math.round(oiBase * (i <= 0 ? 1.12 : 0.88) + Math.random() * 40000);
    return {
      strike,
      call: { oi: cOI, oiChg: Math.round((Math.random() - 0.35) * 35000), volume: Math.round(cOI * 0.11 + Math.random() * 8000), iv: callIV, delta: cDelta, ltp: cLtp, ltpChg: +(Math.random() * 9 - 3.5).toFixed(2) },
      put:  { oi: pOI, oiChg: Math.round((Math.random() - 0.35) * 35000), volume: Math.round(pOI * 0.11 + Math.random() * 8000), iv: putIV,  delta: pDelta, ltp: pLtp, ltpChg: +(Math.random() * 9 - 3.5).toFixed(2) },
    };
  });
}

export function ocExpiryLabel(type: OcExpiry): { short: string; full: string; dte: number } {
  const now = new Date();
  const daysToThursday = ((4 - now.getDay() + 7) % 7) || 7;
  const thu1 = new Date(now); thu1.setDate(thu1.getDate() + daysToThursday);
  const thu2 = new Date(thu1); thu2.setDate(thu2.getDate() + 7);
  const lastOfMonth     = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const lastOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);
  const lastThu = (d: Date) => { const off = (d.getDay() - 4 + 7) % 7; const t = new Date(d); t.setDate(d.getDate() - off); return t; };
  const thu3 = lastThu(lastOfMonth);
  const thu4 = lastThu(lastOfNextMonth);
  const fmt  = (d: Date) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  const dte  = (d: Date) => Math.max(1, Math.round((d.getTime() - now.getTime()) / 86400000));
  const map: Record<OcExpiry, Date> = { current: thu1, next: thu2, monthly: thu3, far: thu4 };
  const d = map[type];
  return { short: fmt(d), full: `${fmt(d)} · ${dte(d)}d to expiry`, dte: dte(d) };
}

// MAX PAIN FIX (07-10 audit): purani local math me CE/PE payout roles ULTE
// the — max(0, K−P)×callOI + max(0, P−K)×putOI, jo exactly swapped hai.
// Ab shared correct implementation (src/lib/options.ts) delegate hota hai.
export function calcMaxPain(chain: OcStrike[]): number {
  return computeMaxPain(chain.map(r => ({
    strike: r.strike, ceOi: r.call.oi, peOi: r.put.oi,
  })));
}
